import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Alert from "../../../Common/Alert";
import DatatableFunc from "../../../Common/DatatableFunc";
import Http from "../../../Common/Http";
import Swal from "sweetalert2";
import IsLogin from "../../../Common/IsLogin";
import { ProgressSpinner } from "primereact/progressspinner";
import ReportDateRangeCsv from "../../../Common/ReportDateRangeCSV";

const SelectionDailyProductionReport = () => {
  const location = useLocation();
  const [receiveData, setReceiveData] = useState([]);
  const [alert, setAlert] = useState(null);
  const date = new Date();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const [inputs, setInputs] = useState({
    date_from: getCurrentDate(),
    date_to: getCurrentDate(),
  });
  const [loading, setLoading] = useState(false);

  const defectTypes = [
    "BN",
    "IS",
    "PH",
    "GD",
    "UG",
    "SS",
    "CR",
    "HC",
    "DC",
    "ST",
    "UD",
    "LT",
    "BC",
    "KD",
    "FD",
    "RC",
    "AIR",
  ];

  const repairTypes = ["PH", "SS", "AIR", "HC"];

  function getCurrentDate() {
    const dateObj = new Date();
    const month = ("0" + (dateObj.getMonth() + 1)).slice(-2);
    const day = ("0" + dateObj.getDate()).slice(-2);
    const year = dateObj.getFullYear();
    return `${year}-${month}-${day}`;
  }

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setInputs((values) => ({ ...values, [name]: value }));
  };
  const fetchData = (dateFromParam, dateToParam) => {
    const dateFrom = dateFromParam || inputs.date_from || getCurrentDate();
    const dateTo = dateToParam || inputs.date_to || getCurrentDate();
    setLoading(true);
    Http.get(
      `/production-and-transfer-list?date_from=${dateFrom}&date_to=${dateTo}`,
    )
      .then((res) => {
        setReceiveData(res.data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    DatatableFunc();
    fetchData();
  }, []);

  const showAlert = (message, type) => {
    setAlert({
      message: message,
      type: type,
    });
    setTimeout(() => {
      setAlert(null);
    }, 4000);
  };

  useEffect(() => {
    if (!location.state) {
      return;
    }
    showAlert(location.state.message, location.state.type);
  }, [location]);

  const formSubmit = () => {
    fetchData(inputs.date_from, inputs.date_to);
  };

  const calculateRowValues = (item) => {
    const productionData = item.production_data || [];

    const exportQty = productionData.reduce(
      (sum, p) => sum + Number(p.qty_export || 0),
      0,
    );

    const aClass = productionData.reduce(
      (sum, p) => sum + Number(p.qty_a_class || 0),
      0,
    );

    const bClass = productionData.reduce(
      (sum, p) => sum + Number(p.qty_b_class || 0),
      0,
    );

    const cClass = productionData.reduce(
      (sum, p) => sum + Number(p.qty_c_class || 0),
      0,
    );

    const totalQty = aClass + bClass + cClass;

    const cClassDefects = item.c_class_defect_details || [];

    const defectCounts = defectTypes.reduce((acc, type) => {
      const totalForType = cClassDefects.reduce((sum, d) => {
        if (d.type === type) {
          return sum + Number(d.qty || 0);
        }
        return sum;
      }, 0);
      acc[type] = totalForType;
      return acc;
    }, {});

    const repairTransferData = item.repair_transfer_data || [];
    const repairReceiveData = item.repair_receive_data || [];

    const repairCounts = repairTypes.reduce((acc, type) => {
      const fromTransfer = repairTransferData.reduce((sum, r) => {
        if (r.type_heading === type) {
          const qty =
            r.delivery_qty != null
              ? Number(r.delivery_qty)
              : Number(r.receive_qty || 0);
          return sum + qty;
        }
        return sum;
      }, 0);

      const fromReceive = repairReceiveData.reduce((sum, r) => {
        if (r.type_heading === type) {
          const qty =
            r.receive_qty != null
              ? Number(r.receive_qty)
              : Number(r.delivery_qty || 0);
          return sum + qty;
        }
        return sum;
      }, 0);

      acc[type] = fromTransfer + fromReceive;
      return acc;
    }, {});

    return {
      exportQty,
      totalQty,
      aClass,
      bClass,
      cClass,
      defectCounts,
      repairCounts,
    };
  };

  const totals = receiveData.reduce(
    (acc, item) => {
      const {
        exportQty,
        totalQty,
        aClass,
        bClass,
        cClass,
        defectCounts,
        repairCounts,
      } = calculateRowValues(item);

      const defectTotals = { ...acc.defectTotals };
      defectTypes.forEach((type) => {
        defectTotals[type] =
          (defectTotals[type] || 0) + (defectCounts[type] || 0);
      });

      const repairTotals = { ...acc.repairTotals };
      repairTypes.forEach((type) => {
        repairTotals[type] =
          (repairTotals[type] || 0) + (repairCounts[type] || 0);
      });

      return {
        totalExportQty: acc.totalExportQty + exportQty,
        totalTotalQty: acc.totalTotalQty + totalQty,
        totalAClass: acc.totalAClass + aClass,
        totalBClass: acc.totalBClass + bClass,
        totalCClass: acc.totalCClass + cClass,
        defectTotals,
        repairTotals,
      };
    },
    {
      totalExportQty: 0,
      totalTotalQty: 0,
      totalAClass: 0,
      totalBClass: 0,
      totalCClass: 0,
      defectTotals: defectTypes.reduce((obj, type) => {
        obj[type] = 0;
        return obj;
      }, {}),
      repairTotals: repairTypes.reduce((obj, type) => {
        obj[type] = 0;
        return obj;
      }, {}),
    },
  );

  const getCsvData = () => {
    const headers = [
      "Name of Ware",
      "Shape",
      "Glaze",
      "Export",
      "Total Qty (A+B+C)",
      "A Class",
      "B Class",
      "C Class",
      ...defectTypes,
      ...repairTypes,
    ];

    const rows = receiveData.map((item) => {
      const {
        exportQty,
        totalQty,
        aClass,
        bClass,
        cClass,
        defectCounts,
        repairCounts,
      } = calculateRowValues(item);
      return [
        item.product_name,
        item.shape,
        item.glaze,
        exportQty,
        totalQty,
        aClass,
        bClass,
        cClass,
        ...defectTypes.map((type) => defectCounts[type] || 0),
        ...repairTypes.map((type) => repairCounts[type] || 0),
      ];
    });

    const footerRows = [
      [
        "",
        "",
        "Total",
        totals.totalExportQty,
        totals.totalTotalQty,
        totals.totalAClass,
        totals.totalBClass,
        totals.totalCClass,
        ...defectTypes.map((type) => totals.defectTotals[type]),
        ...repairTypes.map((type) => totals.repairTotals[type]),
      ],
    ];

    return { headers, rows, footerRows };
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <div>
        <div className="am-mainpanel">
          <div className="am-pagetitle">
            <h5 className="am-title">Selection Daily Report</h5>
          </div>
          <div className="am-pagebody">
            <Alert alert={alert} />
            <div className="card pd-20 pd-sm-40">
              <ReportDateRangeCsv
                dateFrom={inputs.date_from}
                dateTo={inputs.date_to}
                onChange={handleChange}
                onSearch={formSubmit}
                csvFilename={`daily-production-report-bkl_${inputs.date_from}_to_${inputs.date_to}.csv`}
                getCsvData={getCsvData}
              />
            </div>
          </div>
          <div className="am-pagebody">
            <div className="card pd-20 pd-sm-40">
              <div className="row">
                <div className="col-12">
                  <div className="text-center">
                    <h5 className="text-uppercase text-sm font-weight-bolder">
                      Artisan Ceramics Limited
                    </h5>
                    <p>
                      House-16, Road-07, Sector-01, Uttara, Dhaka
                      <br />
                      <b>Selection Daily Report</b>
                      <br />
                      (Glazing Selection)
                    </p>
                  </div>
                  <div
                    className="table-responsive p-0 pb-2"
                    style={{ maxHeight: "70vh", overflowY: "auto" }}
                  >
                    <table className="table table-sm table-bordered table-striped align-items-center justify-content-center mb-0 _datatable">
                      <thead
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 2,
                          backgroundColor: "#fff",
                        }}
                      >
                        <tr>
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2"
                            rowSpan={2}
                          >
                            Name of Ware
                          </th>
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            rowSpan={2}
                          >
                            Shape
                          </th>
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            rowSpan={2}
                          >
                            Glaze
                          </th>
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            rowSpan={2}
                          >
                            Export
                          </th>
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            rowSpan={2}
                          >
                            Total Qty (A+B+C)
                          </th>
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            rowSpan={2}
                          >
                            A Class
                          </th>
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            rowSpan={2}
                          >
                            B Class
                          </th>
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            rowSpan={2}
                          >
                            C Class
                          </th>
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            colSpan={defectTypes.length}
                          >
                            Defects
                          </th>
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            colSpan={repairTypes.length}
                          >
                            Repairs
                          </th>
                        </tr>
                        <tr>
                          {defectTypes.map((type) => (
                            <th
                              key={type}
                              className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            >
                              {type}
                            </th>
                          ))}
                          {repairTypes.map((type) => (
                            <th
                              key={type}
                              className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            >
                              {type}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        {loading ? (
                          <tr>
                            <td
                              className="text-center"
                              colSpan={29}
                              style={{ padding: "2rem" }}
                            >
                              <ProgressSpinner
                                style={{ width: "40px", height: "40px" }}
                                strokeWidth="4"
                              />
                            </td>
                          </tr>
                        ) : receiveData && receiveData.length > 0 ? (
                          receiveData.map((item, index) => {
                            const {
                              exportQty,
                              totalQty,
                              aClass,
                              bClass,
                              cClass,
                              defectCounts,
                              repairCounts,
                            } = calculateRowValues(item);

                            return (
                              <tr key={index}>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {item.product_name}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {item.shape}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {item.glaze}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {exportQty}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {totalQty}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {aClass}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {bClass}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {cClass}
                                </td>
                                {defectTypes.map((type) => (
                                  <td
                                    key={type}
                                    className="text-uppercase text-dark text-sm font-weight-bolder ps-2"
                                  >
                                    {defectCounts[type] || 0}
                                  </td>
                                ))}
                                {repairTypes.map((type) => (
                                  <td
                                    key={type}
                                    className="text-uppercase text-dark text-sm font-weight-bolder ps-2"
                                  >
                                    {repairCounts[type] || 0}
                                  </td>
                                ))}
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td
                              className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                              colSpan={
                                8 + defectTypes.length + repairTypes.length
                              }
                            >
                              No data found
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2"
                            colSpan={3}
                          >
                            Total
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalExportQty}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalTotalQty}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalAClass}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalBClass}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalCClass}
                          </td>
                          {defectTypes.map((type) => (
                            <td
                              key={type}
                              className="text-uppercase text-dark text-sm font-weight-bolder ps-2"
                            >
                              {totals.defectTotals[type]}
                            </td>
                          ))}
                          {repairTypes.map((type) => (
                            <td
                              key={type}
                              className="text-uppercase text-dark text-sm font-weight-bolder ps-2"
                            >
                              {totals.repairTotals[type]}
                            </td>
                          ))}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectionDailyProductionReport;
